import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Calendar, PlayCircle } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/sections/Footer";
import { CookieBanner } from "../components/CookieBanner";
import { useLanguage } from "../contexts/LanguageContext";
import { api, resolveMediaUrl } from "../lib/api";
import { applySEO } from "../lib/seo";
import { trackPageView } from "../lib/analytics";
import { renderRichText } from "../lib/richText";

export default function NewsDetail() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([api.get(`/news/${id}`), api.get("/news")])
      .then(([article, list]) => {
        setArticle(article.data);
        setAllItems(Array.isArray(list.data) ? list.data : []);
      })
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  // SEO + analytics
  useEffect(() => {
    if (!article) return;
    const title = lang === "fr" ? article.title_fr : article.title_en;
    const description = lang === "fr" ? article.excerpt_fr : article.excerpt_en;
    applySEO({
      title,
      description,
      image: article.image_url ? resolveMediaUrl(article.image_url) : undefined,
      url: `https://richildinvest.com/news/${article.id}`,
      type: "article",
      lang,
      publishedTime: article.created_at,
    });
    trackPageView(`/news/${article.id}`);
  }, [article, lang]);

  const related = useMemo(() => {
    if (!article || !allItems.length) return [];
    return allItems.filter((x) => x.id !== article.id).slice(0, 3);
  }, [article, allItems]);

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <div data-testid="news-detail-page" className="bg-white min-h-screen overflow-x-hidden">
      <Header />

      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          {/* Back button */}
          <Link
            to="/#news"
            data-testid="news-back-btn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#0D2F4C]/70 hover:text-[#0D2F4C] transition-colors mb-12 group"
          >
            <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
            <span>{t.news.back}</span>
          </Link>

          {loading && (
            <div className="space-y-6" data-testid="news-detail-loading">
              <div className="h-6 w-32 bg-[#F8F9FA] animate-pulse" />
              <div className="h-16 bg-[#F8F9FA] animate-pulse" />
              <div className="aspect-[16/9] bg-[#F8F9FA] animate-pulse" />
              <div className="h-4 bg-[#F8F9FA] animate-pulse" />
              <div className="h-4 bg-[#F8F9FA] animate-pulse w-4/5" />
            </div>
          )}

          {!loading && notFound && (
            <div data-testid="news-not-found" className="text-center py-20 border border-[#0D2F4C]/10">
              <p className="text-[#4B5563] mb-6">{t.news.not_found}</p>
              <button
                onClick={() => navigate("/#news")}
                className="text-sm text-[#0D2F4C] underline"
              >
                {t.news.back_to_list}
              </button>
            </div>
          )}

          {!loading && article && (
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              data-testid={`news-article-${article.id}`}
            >
              {/* Meta header */}
              <div className="flex items-center gap-4 mb-6">
                <span
                  className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#0D2F4C]"
                  data-testid="article-category"
                >
                  {lang === "fr" ? article.category_fr : article.category_en}
                </span>
                <span className="h-px w-12 bg-[#0D2F4C]/30" />
                <span className="text-xs text-[#4B5563] inline-flex items-center gap-1.5">
                  <Calendar size={12} />
                  {formatDate(article.created_at)}
                </span>
              </div>

              {/* Title */}
              <h1
                data-testid="article-title"
                className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#0D2F4C] tracking-tight mb-10"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {lang === "fr" ? article.title_fr : article.title_en}
              </h1>

              {/* Excerpt */}
              <p className="text-lg sm:text-xl leading-relaxed text-[#4B5563] mb-12 max-w-3xl">
                {lang === "fr" ? article.excerpt_fr : article.excerpt_en}
              </p>

              {/* Hero image */}
              {article.image_url && (
                <div className="aspect-[16/9] overflow-hidden bg-[#F8F9FA] mb-14">
                  <img
                    src={resolveMediaUrl(article.image_url)}
                    alt={lang === "fr" ? article.title_fr : article.title_en}
                    className="w-full h-full object-cover"
                    data-testid="article-image"
                    loading="lazy"
                  />
                </div>
              )}

              {/* External video CTA (when video_url points to an external host) */}
              {article.video_url &&
                /^https?:\/\//i.test(article.video_url) && (
                  <a
                    href={article.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="article-video-cta"
                    className="inline-flex items-center gap-3 bg-[#0D2F4C] text-white px-6 py-3.5 mb-12 text-sm tracking-wide hover:bg-[#0A243A] transition-colors group"
                    style={{ borderRadius: 0 }}
                  >
                    <PlayCircle size={18} strokeWidth={1.7} className="text-[#C9A961]" />
                    <span>
                      {lang === "fr" ? "Regarder la vidéo" : "Watch the video"}
                    </span>
                    <ArrowUpRight
                      size={14}
                      className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </a>
                )}

              {/* Body */}
              <div
                data-testid="article-content"
                className="prose-style text-[#0D2F4C] text-base sm:text-lg leading-[1.85]"
              >
                {renderRichText(lang === "fr" ? article.content_fr : article.content_en)}
              </div>

              {/* Footer signature */}
              <div className="border-t border-[#0D2F4C]/10 mt-16 pt-8 flex items-center justify-between gap-4">
                <div className="text-xs uppercase tracking-[0.25em] text-[#0D2F4C]/60">
                  Richild Invest GBC · {formatDate(article.created_at)}
                </div>
                <Link
                  to="/#contact"
                  className="text-sm text-[#0D2F4C] hover:underline inline-flex items-center gap-2"
                >
                  <span>{t.nav.contact}</span>
                  <ArrowUpRight size={14} />
                </Link>
              </div>
            </motion.article>
          )}
        </div>

        {/* Related articles */}
        {!loading && article && related.length > 0 && (
          <section
            data-testid="related-articles"
            className="max-w-7xl mx-auto px-6 sm:px-8 mt-24"
          >
            <div className="flex items-center gap-4 mb-10">
              <span className="h-px w-10 bg-[#0D2F4C]" />
              <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#0D2F4C]">
                {t.news.related}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  data-testid={`related-card-${item.id}`}
                >
                  <Link to={`/news/${item.id}`} className="group block">
                    <div className="aspect-[4/3] overflow-hidden bg-[#F8F9FA] mb-5">
                      {item.image_url ? (
                        <img
                          src={resolveMediaUrl(item.image_url)}
                          alt={lang === "fr" ? item.title_fr : item.title_en}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#0D2F4C]/10 to-[#0D2F4C]/30" />
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0D2F4C] mb-2">
                      {lang === "fr" ? item.category_fr : item.category_en}
                    </div>
                    <h3
                      className="font-serif text-xl text-[#0D2F4C] leading-tight group-hover:opacity-70 transition-opacity"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {lang === "fr" ? item.title_fr : item.title_en}
                    </h3>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
}
